import { useState } from "react";
import { t } from "@lingui/core/macro";
import { useForm } from "react-hook-form";

import { Button } from "_/components/button";
import {
  Hardware,
  Machine,
  NewMachine,
  useCreateMachine,
  useEditMachine,
  useMachinesByOrgs,
  useTransferHardware,
} from "_/data/machines";
import { AdminMachineListing } from "_/components/machine-listing";
import { SubmitCancelButtons, useModal } from "_/components/modal";
import { FormTextField } from "_/components/text-field";
import { FormSelect } from "_/components/select";
import { FilterSelect } from "_/components/admin-view/filterSelect";
import { FormField } from "_/components/form-field";
import { PermissionTest, testPermissions } from "_/components/permission-test";
import { useAddToast } from "_/components/toasts";
import { useLocation } from "_/components/router";
import { Form } from "_/components/form";
import {
  MachineEditForm,
  getChannelOptions,
} from "_/components/machine-edit/machine-edit-form";

import { Org, useAon3dOrgs, useFactoryOrg, useOrgs } from "_/data/orgs";
import { ROOT_DOMAIN, useCurrentUserPermissions } from "_/data/rbac";
import { useChannels } from "_/data/channels";

import { emailSupport } from "_/utils";
import { SERIAL_NUMBER_REGEX } from "_/utils/consts";
import { dateOnlyString } from "_/utils/datetime";
import { useSetLocationAndOrg } from "_/hooks/useSetLocationAndOrg";

import { routeUrls } from "_/routes";

import { AdminPage } from "./admin-page";

import * as S from "./styled";

/**
 * Form for creating a new machine, or editing existing machine details..
 */
const MachineCreateForm = ({
  onClose,
}: {
  machine?: Machine;
  onClose: () => void;
}) => {
  const createMachine = useCreateMachine();
  const { data: factoryOrg } = useFactoryOrg();
  const setLocationAndOrg = useSetLocationAndOrg();
  const addToast = useAddToast();
  const { data: channels = [] } = useChannels();

  const defaultValues = {
    serial: "",
    updateChannel: "stable",
  };

  const channelOptions = getChannelOptions(channels);

  type FormData = Pick<Hardware, "serial"> & Pick<Machine, "updateChannel">;

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormData>({ defaultValues });

  const onSubmit = handleSubmit(async (data: FormData) => {
    if (!factoryOrg) {
      throw new Error("Factory org is required for machine creation.");
    }

    const { serial, updateChannel } = data;

    const newMachine: NewMachine = {
      serial,
      updateChannel,
      orgId: factoryOrg.id,
    };

    await createMachine.mutateAsync({
      newMachine,
      onSuccess: (machine) => {
        setLocationAndOrg({
          orgId: factoryOrg.id,
          url: routeUrls.machine.index(machine.id),
        });
      },
      onError: ({ statusCode: rawStatusCode, message }) => {
        const statusCode = rawStatusCode ?? "unknown";
        const msg = message || t`common.http-error-code ${statusCode}`;
        const title =
          rawStatusCode === 403
            ? t`components.admin-view.action-permission-denied`
            : t`components.admin-view.machine-creation-failed ${msg}`;
        addToast({
          title,
          kind: "warning",
          timeout: 5_000,
        });
      },
    });
    onClose();
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormTextField
        control={control}
        name="serial"
        label={t`common.serial-number`}
        placeholder={t`common.serial-number`}
        rules={{
          required: t`components.admin-view.serial-number-required`,
          pattern: {
            value: SERIAL_NUMBER_REGEX,
            message: t`components.admin-view.serial-number-invalid`,
          },
        }}
      />
      <FormSelect
        control={control}
        name="updateChannel"
        label={t`common.software-channel`}
        options={channelOptions}
        isSearchable={false}
      />
      <SubmitCancelButtons onCancel={onClose} disableSubmit={!isDirty} />
    </Form>
  );
};

const MachineRenameForm = ({
  machine,
  onClose,
}: {
  machine: Machine;
  onClose: () => void;
}) => {
  const renameMachine = useEditMachine();
  const addToast = useAddToast();

  const defaultValues = {
    name: machine.name,
  };
  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({ defaultValues });

  const onSubmit = handleSubmit(async (data) => {
    await renameMachine.mutateAsync({
      id: machine.id,
      params: {
        name: data.name,
      },
      onError: ({ statusCode: rawStatusCode, message }) => {
        const statusCode = rawStatusCode ?? "unknown";
        const msg = message || t`common.http-error-code ${statusCode}`;
        const title =
          rawStatusCode === 403
            ? t`components.admin-view.action-permission-denied`
            : t`components.admin-view.machine-update-failed ${msg}`;
        addToast({
          title,
          kind: "warning",
          timeout: 5_000,
        });
      },
    });
    onClose();
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormField label={t`common.serial-number`}>
        {machine.hardware.serial}
      </FormField>
      <FormField label={t`common.machine-manufacture-date`}>
        {dateOnlyString({ value: machine.hardware.manufactureDate })}
      </FormField>
      <FormTextField
        control={control}
        name="name"
        label={t`common.name`}
        placeholder={t`common.name`}
        rules={{ required: t`components.admin-view.name-required` }}
      />
      <SubmitCancelButtons onCancel={onClose} disableSubmit={!isDirty} />
    </Form>
  );
};

/**
 * Form for transferring machine hardware from one organization to another.
 */
const HardwareTransferForm = ({
  machine,
  onClose,
  transferToOrgs,
}: {
  machine: Machine;
  onClose: () => void;
  transferToOrgs: Org[];
}) => {
  const transferHardware = useTransferHardware();
  const defaultOrgId =
    transferToOrgs.length === 1 ? transferToOrgs[0].id : undefined;
  const defaultValues = { orgId: defaultOrgId };
  const { control, handleSubmit, formState } = useForm({ defaultValues });
  const addToast = useAddToast();
  const [_, setLocation] = useLocation();

  if (!transferToOrgs.length) {
    return (
      <S.ErrorMessage>
        {t`components.admin-view.no-orgs-to-transfer-to`}
      </S.ErrorMessage>
    );
  }

  const onSubmit = handleSubmit(async (data) => {
    if (!data.orgId) {
      console.error("No org ID provided (unreachable).");
      return;
    }

    const targetOrg = transferToOrgs.find((o) => o.id === data.orgId);
    if (!targetOrg) {
      console.error("Target org not found (unreachable).");
      return;
    }

    await transferHardware.mutateAsync({
      machineId: machine.id,
      orgId: targetOrg.id,
      onSuccess: (machineInner) => {
        addToast({
          title: t`components.admin-view.transfer-toast.title`,
          kind: "success",
          content: t`components.admin-view.transfer-toast.content ${targetOrg.name}`,
          timeout: 10_000,
          actions: [
            {
              label: t`components.admin-view.transfer-toast.view-machine-label`,
              action: () =>
                setLocation(routeUrls.machine.index(machineInner.id)),
            },
          ],
        });
      },
      onError: ({ statusCode: rawStatusCode, message }) => {
        const statusCode = rawStatusCode ?? "unknown";
        const msg = message || t`common.http-error-code ${statusCode}`;
        const title =
          rawStatusCode === 403
            ? t`components.admin-view.action-permission-denied`
            : t`components.admin-view.machine-transfer-failed ${msg}`;
        addToast({
          title,
          kind: "warning",
          timeout: 5_000,
        });
      },
    });

    onClose();
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormField label={t`common.serial-number`}>
        {machine.hardware.serial}
      </FormField>
      <FormField label={t`common.name`}>{machine.name}</FormField>
      <FormField label={t`common.machine-manufacture-date`}>
        {dateOnlyString({ value: machine.hardware.manufactureDate })}
      </FormField>
      {transferToOrgs.length > 1 ? (
        <FormSelect
          control={control}
          name="orgId"
          label={t`common.admin-view.transfer-destination-org`}
          options={transferToOrgs.map((o) => ({
            label: o.name,
            value: o.id,
          }))}
          required
        />
      ) : (
        <FormField label={t`common.admin-view.transfer-destination-org`}>
          {transferToOrgs[0].name}
        </FormField>
      )}
      <SubmitCancelButtons
        submitButtonLabel={t`components.admin-view.transfer-button-label`}
        onCancel={onClose}
        disableSubmit={!formState.isDirty && transferToOrgs.length > 1}
      />
    </Form>
  );
};

/**
 * What kind of machine list is this?
 *
 * - Factory machines - machines in the Manufacturing org, and where all new machines are created.
 * - Other AON3D machines - machines in other AON3D orgs, excluding the Manufacturing org
 * - Customer machines - machines in customer orgs
 * - Detached machines - machines that were transferred out of an org. These machines contain history
 *   of the machine but no hardware.
 * - Org detail machines - machines in a specific org, shown on that org's detail page
 */
type MachineListVariant =
  | "factory"
  | "otherAon3d"
  | "customer"
  | "detached"
  | "orgDetail";

type AdminMachinesSectionProps =
  | {
      variant: Exclude<MachineListVariant, "orgDetail">;
      orgDetail?: never;
      filter?: (machine: Machine, org: Org) => boolean;
    }
  | {
      variant: "orgDetail";
      orgDetail: Org;
      filter?: never;
    };

/**
 * One of the variations of machine list + heading section in admin tools UI.
 */
export const AdminMachinesSection = ({
  variant,
  orgDetail,
  filter,
}: AdminMachinesSectionProps) => {
  const editModal = useModal<Machine>();
  const createModal = useModal<undefined>();
  const renameModal = useModal<Machine | undefined>();
  const transferModal = useModal<Machine>();

  const { data: orgs = [] } = useOrgs();
  const { data: aon3dOrgs = [] } = useAon3dOrgs();
  const { data: factoryOrg, isLoading: factoryOrgLoading } = useFactoryOrg();
  const { data: orgOrRootPermissions } = useCurrentUserPermissions(
    orgDetail?.domainId ?? ROOT_DOMAIN
  );
  const { data: rootPermissions } = useCurrentUserPermissions(ROOT_DOMAIN);

  if (!factoryOrgLoading && !factoryOrg && variant !== "orgDetail") {
    return (
      <S.ErrorMessage>{t`components.admin-view.factory-org-not-found`}</S.ErrorMessage>
    );
  }

  const otherAon3dOrgs = factoryOrg
    ? aon3dOrgs.filter((o) => o.id !== factoryOrg.id)
    : [];
  const aon3dOrgIds = aon3dOrgs.map((o) => o.id);
  const customerOrgs = orgs.filter((o) => !aon3dOrgIds.includes(o.id));

  // TODO: non-factory variants always transfer to exactly one org (the factory),
  // so transferToOrgs could be Org | null for those cases instead of Org[].
  // Left as-is for now — worth addressing in a broader refactor of how factory
  // org identity is modelled across the client (HOLO-1423).
  const factoryOrgList = factoryOrg ? [factoryOrg] : [];

  const variantInfo: {
    /* Section heading in UI for this kind of machines */
    title: string;

    /* Orgs whose machines should be shown for this variant */
    orgs: Org[];

    /**
     * Where the user is allowed to transfer machines.
     *
     * - Factory org machines can be transferred to any non-factory org ("Transfer to Customer").
     * - All other org machines can be transferred to the factory org ("Transfer to Factory").
     */
    transferToOrgs?: Org[];
    /**
     * Either "Transfer to Customer" (if machine is in the factory org) or
     * "Transfer to Factory" (if machine is in a non-factory org)
     * */
    transferLabel?: string;
  } = (
    {
      factory: {
        title: t`components.admin-view.factory-machines`,
        orgs: factoryOrgList,
        transferToOrgs: factoryOrg
          ? orgs.filter((o) => o.id !== factoryOrg.id)
          : [],
        transferLabel: t`components.admin-view.transfer-to-customer`,
      },
      otherAon3d: {
        title: t`components.admin-view.aon3d-machines`,
        orgs: otherAon3dOrgs,
        transferToOrgs: factoryOrgList,
        transferLabel: t`components.admin-view.transfer-to-factory`,
      },
      customer: {
        title: t`components.admin-view.customer-machines`,
        orgs: customerOrgs,
        transferToOrgs: factoryOrgList,
        transferLabel: t`components.admin-view.transfer-to-factory`,
      },
      detached: {
        title: t`common.detached-machines`,
        orgs,
      },
      orgDetail: {
        title: t`common.machines`,
        // `orgDetail` always exists when variant is "orgDetail", but TS doesn't
        // know that here.
        orgs: orgDetail ? [orgDetail] : [],
        transferToOrgs:
          orgDetail?.id === factoryOrg?.id
            ? orgs.filter((o) => o.id !== factoryOrg?.id)
            : factoryOrgList,
        transferLabel:
          orgDetail?.id === factoryOrg?.id
            ? t`components.admin-view.transfer-to-customer`
            : t`components.admin-view.transfer-to-factory`,
      },
    } as const
  )[variant];

  const actions = (machine: Machine) =>
    [
      {
        render: t`common.support`,
        onClick: () => {
          emailSupport(`Basis Machine Support [${machine.hardware.serial}]`);
        },
      },
      testPermissions("machines/write", orgOrRootPermissions) && {
        render: t`common.rename`,
        onClick: () => renameModal.openModal(machine),
      },
      // For setting software settings and transferring, we'll be conservative
      // and require root domain permissions. In theory someone could have these
      // permissions for just one org, but in practice the only roles that have
      // these permissions are global roles on the root domain. In the future if
      // we want to make it more complicated (like an outsourced manufacturer or
      // support org) then we can revise this.
      testPermissions("machines/setChannel", rootPermissions) && {
        render: t`components.admin-view.software-updates`,
        onClick: () => editModal.openModal(machine),
      },
      testPermissions("hardware/transfer", rootPermissions) &&
        variantInfo.transferToOrgs &&
        variantInfo.transferLabel && {
          render: variantInfo.transferLabel,
          onClick: () => transferModal.openModal(machine),
        },
    ].filter(Boolean);

  return (
    <S.AdminContentSection>
      <S.AdminContentSectionTitleContainer>
        <h2>{variantInfo.title}</h2>
        {variant === "factory" && (
          <PermissionTest
            domainId={ROOT_DOMAIN}
            requiredPermissions="machines/create"
            render={({ allowed }) => (
              <Button
                onClick={() => createModal.openModal(undefined)}
                disabled={!allowed}
              >{t`components.admin-view.create-machine`}</Button>
            )}
          />
        )}
      </S.AdminContentSectionTitleContainer>
      <AdminMachineListing
        orgs={variantInfo.orgs}
        actions={actions}
        showOrg={variant !== "factory" && variant !== "orgDetail"}
        detached={variant === "detached"}
        filter={filter}
      />
      <editModal.Modal
        title={
          editModal.modalData
            ? t`components.admin-view.software-updates`
            : t`components.admin-view.create-machine`
        }
      >
        {editModal.isModalOpen && (
          <MachineEditForm
            onClose={editModal.closeModal}
            machine={editModal.modalData}
          />
        )}
      </editModal.Modal>
      <createModal.Modal
        title={
          createModal.modalData
            ? t`components.admin-view.rename-machine`
            : t`components.admin-view.create-machine`
        }
      >
        {createModal.isModalOpen && (
          <MachineCreateForm onClose={createModal.closeModal} />
        )}
      </createModal.Modal>
      <renameModal.Modal title={t`components.admin-view.rename-machine`}>
        {renameModal.modalData && (
          <MachineRenameForm
            onClose={renameModal.closeModal}
            machine={renameModal.modalData}
          />
        )}
      </renameModal.Modal>
      {variantInfo.transferToOrgs && (
        <transferModal.Modal title={t`components.admin-view.transfer-machine`}>
          {transferModal.modalData && (
            <HardwareTransferForm
              onClose={transferModal.closeModal}
              machine={transferModal.modalData}
              transferToOrgs={variantInfo.transferToOrgs}
            />
          )}
        </transferModal.Modal>
      )}
    </S.AdminContentSection>
  );
};

export const Machines = () => {
  const { data: orgs = [] } = useOrgs();
  const { data: machines = [] } = useMachinesByOrgs(orgs);
  const [serialFilter, setSerialFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");

  const filter = (machine: Machine, org: Org) => {
    const matchesSerial = machine.hardware.serial
      .toLowerCase()
      .includes(serialFilter.toLowerCase());
    const matchesOrg = org.name.toLowerCase().includes(orgFilter.toLowerCase());
    return matchesSerial && matchesOrg;
  };

  // Dedupe serial numbers and org names for filter options. It's possible to
  // have multiple machines with the same serial number if a machine was
  // transferred out of an org (the machine will still exist but without
  // hardware, so it won't show up in the listing but could be in the filter
  // options), and it's possible to have multiple orgs with the same name
  // (though unlikely and a really bad idea!).
  const serialOptions = [
    ...new Set(machines.map((m) => m.machine.hardware.serial).toSorted()),
  ].map((serial) => ({ label: serial, value: serial }));
  const orgOptions = [...new Set(orgs.map((o) => o.name).toSorted())].map(
    (orgName) => ({
      label: orgName,
      value: orgName,
    })
  );

  const filters = (
    <S.FilterContainer>
      <S.OneFilterSerialNumber>
        <FilterSelect
          name="machineSerialFilter"
          options={serialOptions}
          placeholder={t`components.admin-view.filter.serial`}
          filterValue={serialFilter}
          onFilterChange={setSerialFilter}
        />
      </S.OneFilterSerialNumber>
      <S.OneFilterOrg>
        <FilterSelect
          name="orgFilter"
          options={orgOptions}
          placeholder={t`components.admin-view.filter.org`}
          filterValue={orgFilter}
          onFilterChange={setOrgFilter}
        />
      </S.OneFilterOrg>
    </S.FilterContainer>
  );

  return (
    <AdminPage title={t`common.machines`} headerControls={filters}>
      <AdminMachinesSection variant="factory" filter={filter} />
      <AdminMachinesSection variant="otherAon3d" filter={filter} />
      <AdminMachinesSection variant="customer" filter={filter} />
      <AdminMachinesSection variant="detached" filter={filter} />
    </AdminPage>
  );
};
